import {
  Component,
  computed,
  effect,
  ElementRef,
  Inject,
  untracked,
  viewChild,
} from '@angular/core';
import {
  IqChatFrom,
  OutlookIqBoostService,
  OutlookIqBoostStore,
  canRetryAiError,
  classifyAiError,
  getAiErrorTranslationKey,
} from '@maximizer/outlook/ai/data-access';
import { OutlookIqBoostLoaderComponent } from '../loaders/loader.component';
import { MarkdownRenderingPipe, SafeHtmlPipe } from '@maximizer/ai/ui';
import { OutlookDeAnonymizerPipe } from '../../pipes/de-anonymizer.pipe';
import { OutlookClickableRefPipe } from '../../pipes/clickable-ref.pipe';
import { OutlookEntityKeyRefPipe } from '../../pipes/entity-key-ref.pipe';
import { catchError, finalize, of, take, tap } from 'rxjs';
import { sanitizeMarkdownToHtml } from '@maximizer/shared/util';
import { CDN_URL } from '@maximizer/core/shared/domain';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_TOOLTIP } from '@progress/kendo-angular-tooltip';
import { GlobalStore } from '@maximizer/core/shared/data-access';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import {
  EntryStore,
  TrackerService,
} from '@maximizer/outlook/shared/data-access';
import {
  EventAction,
  EventJourney,
  EventModule,
} from '@maximizer/outlook/shared/domain';

@Component({
  selector: 'maximizer-outlook-iq-boost-chat',
  templateUrl: './chat.component.html',
  styles: ':host { display: flex; flex-direction: column; min-height: 0; }',
  imports: [
    KENDO_BUTTON,
    KENDO_TOOLTIP,
    OutlookIqBoostLoaderComponent,
    MarkdownRenderingPipe,
    SafeHtmlPipe,
    OutlookDeAnonymizerPipe,
    OutlookClickableRefPipe,
    OutlookEntityKeyRefPipe,
    TranslatePipe,
    UI_PIPES,
  ],
})
export class OutlookIqBoostChatComponent {
  scrollContainer =
    viewChild.required<ElementRef<HTMLDivElement>>('scrollContainer');

  readonly IqChatFrom = IqChatFrom;

  readonly chatHistory = computed(() => this.iqBoostStore.chatHistory());
  readonly isLoading = computed(() => this.iqBoostStore.loading());
  readonly userName = computed(
    () => this.globalStore.session()?.user.name ?? '',
  );
  readonly error = computed(() => this.iqBoostStore.error());
  readonly errorMessage = computed(() => {
    const errorType = this.error();
    return errorType ? getAiErrorTranslationKey(errorType) : null;
  });
  readonly canRetry = computed(() => {
    const errorType = this.error();
    return errorType ? canRetryAiError(errorType) : false;
  });

  constructor(
    @Inject(CDN_URL) readonly cdn: string,
    private readonly iqBoostService: OutlookIqBoostService,
    private readonly iqBoostStore: OutlookIqBoostStore,
    private readonly tracker: TrackerService,
    private readonly globalStore: GlobalStore,
    private readonly entryStore: EntryStore,
  ) {
    effect(() => {
      const question = this.iqBoostStore.chatPrompt();
      if (!question) return;

      const history = untracked(() => this.iqBoostStore.chatHistory());
      const lastUserMessage = history
        .slice()
        .reverse()
        .find((message) => message.from === IqChatFrom.User);
      if (lastUserMessage?.content === question) return;

      this.iqBoostStore.addChatMessage({
        content: question,
        from: IqChatFrom.User,
      });
      this.iqBoostStore.clearError();
      this.iqBoostStore.setLoading(true);
      this.scrollToBottom();

      this.iqBoostService
        .generateChatResponse(question)
        .pipe(
          take(1),
          tap((text) => {
            this.iqBoostStore.addChatMessage({
              content: sanitizeMarkdownToHtml(text),
              from: IqChatFrom.Ai,
            });
            this.iqBoostStore.setChatResponse(text);
            this.tracker.trackEvent({
              module: EventModule.IqBoost,
              journey: EventJourney.Chat,
              action: EventAction.Completed,
              metadata: { entry: this.entryStore.type() },
            });
          }),
          catchError((err: unknown) => {
            this.iqBoostStore.setError(classifyAiError(err));
            return of(null);
          }),
          finalize(() => this.iqBoostStore.setLoading(false)),
        )
        .subscribe();
    });
  }

  retry(): void {
    const lastUserMessage = this.iqBoostStore
      .chatHistory()
      .slice()
      .reverse()
      .find((message) => message.from === IqChatFrom.User);
    if (!lastUserMessage) return;

    this.iqBoostStore.clearError();
    this.iqBoostStore.setLoading(true);
    this.scrollToBottom();

    this.iqBoostService
      .generateChatResponse(lastUserMessage.content)
      .pipe(
        take(1),
        tap((text) => {
          this.iqBoostStore.addChatMessage({
            content: sanitizeMarkdownToHtml(text),
            from: IqChatFrom.Ai,
          });
          this.iqBoostStore.setChatResponse(text);
        }),
        catchError((err: unknown) => {
          this.iqBoostStore.setError(classifyAiError(err));
          return of(null);
        }),
        finalize(() => this.iqBoostStore.setLoading(false)),
      )
      .subscribe();
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.scrollContainer().nativeElement) {
        this.scrollContainer().nativeElement.scrollTop =
          this.scrollContainer().nativeElement.scrollHeight;
      }
    }, 0);
  }
}
